'use client';
import { useEffect, useState, useCallback } from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Paper, CircularProgress } from '@mui/material';
import { buttonColor, buttonHoverColor, fontfamily, primaryColor } from "../../Util/UtilColorPalate";
import { checkOutApi } from "../../Util/UtilAPi";
import UtilToastMessages from "../../Util/UtilToastMessages";

const Checkout = ({ selectedItems, fetchDetails, companyInfo, domainSelection, onPrevious }) => {
  const currencySymbols = {
    INR: "₹",
    USD: "$",
    ZAR: "R",
  };
  const currencyKey = Object.keys(currencySymbols).find(
    (key) => currencySymbols[key] === companyInfo?.orgCurrency
  );
  const companyDetails = {
    title: companyInfo?.title,
    fullName: companyInfo?.fullname || 'N/A',
    contactNumber: companyInfo?.phone || 'N/A',
    alternateContactNumber: companyInfo?.mobile || 'N/A',
    companyName: companyInfo?.companyname || 'N/A',
    emailAddress: companyInfo?.email || 'N/A',
    currency: companyInfo?.currency || 'INR',
    paymentCurrency:companyInfo?.paymentCurrency,
    orgCurrency: currencyKey
  };
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [exchangeRates, setExchangeRates] = useState({})

  // Fetch exchange rates
  const fetchRates = useCallback(async () => {
    if (currencyKey) {
      try {
        const response = await fetch(`https://open.er-api.com/v6/latest/${currencyKey}`);
        const data = await response.json();
        if (data.result === "success") {
          setExchangeRates(data.rates);
        } else {
          console.error("Error fetching rates:", data);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        setSnackbar({
          open: true,
          message: "Failed to fetch currency rates",
          severity: "error",
        });
      }
    }

  }, [currencyKey]);

  const allItems = selectedItems
    .filter(Boolean)
    .map((item) => {
      const currencyCode = companyDetails.paymentCurrency?.split("-")[0];
      const currencySymbol = companyDetails.paymentCurrency?.split("-")[1];
      const rate = exchangeRates[currencyCode?.trim()] || 1;
      // console.log(rate,item.priceperunit,item.lineitemtotal,"nknknkk")
      return {
        name: item.product?.name || 'Unknown Product',
        quantity: item.quantity || 1,
        currency: currencySymbol,
        price: parseFloat(item.priceperunit * 100) / 100 * parseFloat(rate),
        totalPrice: item.lineitemtotal * parseFloat(rate),
        type: item.bundle === true ? "Bundle" :item.product?.name?.includes("SMS")? "Add on Product":"License",
        id: item.dealdeallineitemsid?.recordid,
        domain:  ""
      };
    });
  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);
  const calculateTotal = () => {
    if (allItems.length === 0) return "0.00"; // Handle empty case
    const currency = allItems[0].currency; // Assuming all items have the same currency
  const total = allItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    return `${currency || "R"} ${total.toFixed(2)}`;
  };

  const handleCheckOut = async () => {
    const payload = {
      recordid: allItems[0].id,
    };
    setIsSubmitting(true);
    try {
      const response = await checkOutApi(payload);
      if (response?.statusCode === 200 || response?.statusCode === 201) {

        // Redirect to Stripe Checkout after showing the message
        setTimeout(() => {
          window.location.href =
            response.data.redirecturl
        }, 2000); // Delay to allow Snackbar to be displayed
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "An error occurred",
        severity: "",
      });
    }
    finally {
      localStorage.clear()
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    fetchDetails()
  }, [])
  useEffect(() => {

    fetchRates();
  }, [currencyKey])
  return (
    <Box>
      {/* Company Details Section */}
      <Box mb={4} borderRadius={2} sx={{ backgroundColor: "none" }}>

        <TableContainer>
          <Table sx={{ border: "none" }}>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography fontFamily={fontfamily} fontWeight="bold" >
                    Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontFamily={fontfamily}>
                    {companyDetails.title} {companyDetails.fullName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" fontFamily={fontfamily} >
                    Company Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontFamily={fontfamily}>{companyDetails.companyName}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography fontWeight="bold" fontFamily={fontfamily} >
                    Contact Number
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontFamily={fontfamily}>{companyDetails.contactNumber}</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" fontFamily={fontfamily} >
                    Alternate Contact Number
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontFamily={fontfamily}>{companyDetails.alternateContactNumber}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography fontWeight="bold" fontFamily={fontfamily}>
                    Email
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontFamily={fontfamily}>{companyDetails.emailAddress}</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" fontFamily={fontfamily} >
                    Payment Currency
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontFamily={fontfamily}>{companyDetails.paymentCurrency}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>


      </Box>

      {/* Products Table Section */}
      <TableContainer component={Paper} sx={{ marginBottom: 4 }}>
        <Table>
          <TableHead sx={{ backgroundColor: primaryColor }}>
            <TableRow>
              <TableCell sx={{ fontFamily: fontfamily, fontWeight: "600", color: 'white' }}>Item Name</TableCell>
              <TableCell sx={{ fontFamily: fontfamily, fontWeight: "600", color: 'white' }} align="center">Type</TableCell>
              <TableCell sx={{ fontFamily: fontfamily, fontWeight: "600", color: 'white' }} align="center">Quantity</TableCell>
              <TableCell sx={{ fontFamily: fontfamily, fontWeight: "600", color: 'white' }} align="center">Price (per unit)</TableCell>
              <TableCell sx={{ fontFamily: fontfamily, fontWeight: "600", color: 'white' }} align="center">Total Price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allItems.map((item, index) => (
              <TableRow key={index}>
                <TableCell sx={{ fontFamily: fontfamily }}>{`${item.name} ${item.domain ? `(${item.domain})` : ""}`}</TableCell>
                <TableCell sx={{ fontFamily: fontfamily }} align="center">{item.type}</TableCell>
                <TableCell sx={{ fontFamily: fontfamily }} align="center">{item.quantity}</TableCell>
                <TableCell sx={{ fontFamily: fontfamily }} align="center">{item.currency || "R"}   {item.price.toFixed(2)} </TableCell>
                <TableCell sx={{ fontFamily: fontfamily }} align="center">
                  {item.currency || "R"}   {(item.totalPrice).toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Total Price */}
      <Box sx={{ textAlign: "end" }}>
        <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: fontfamily }}>
          Total:  {calculateTotal()}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "end" }}>
          {/* <Button
                   variant="outlined"
                   onClick={handlePrevious}
                   sx={{
                     mt: 2,
                     px: 4,
                     py: 1,
                     fontFamily: fontfamily,
                     // borderRadius: 5,
                     color: primaryColor,
                     borderColor: primaryColor,
                   }}
                 >
                   Previous
                 </Button> */}

          <Button
            variant="contained"
            onClick={handleCheckOut}
            disabled={isSubmitting}
            sx={{
              mt: 2,
              px: 4,
              py: 1,
              ml: 1,
              fontFamily: fontfamily,
              // borderRadius: 5,
              backgroundColor: buttonColor,
              "&:hover": { backgroundColor: buttonHoverColor },
              "&:disabled": {
                backgroundColor: "grey.300",
                cursor: "not-allowed",
              },
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={10} color="inherit" style={{ marginRight: 8 }} />
                Pay Now
              </>
            ) : (
              "Pay Now"
            )}
          </Button>
        </Box>
      </Box>

      <UtilToastMessages
        isOpen={snackbar.open}
        handleClose={handleSnackbarClose}
        data={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default Checkout;